import { useEffect, useState } from "react";
import { useParams } from "react-router";
import classNames from "classnames";

import { secondsToTimeString } from "helpers/utility/formatters";

import ImagePreview from "views/pages/EnrichmentPage/ImagePreview";
import ActionButtons from "./ActionButtons";
import ErrorIcon from "./Icons/ErrorIcon";
import Loader from "views/uikit/Loader";
import VideoBox from "./VideoBox";

import styles from "./style.module.scss";

const ErrorPreview = () => {
  return (
    <div className={styles.errorPreview}>
      <ErrorIcon />
      preview is not available
    </div>
  );
};

interface BodyProps {
  imageSrc: string;
  linkClicked: any;
  timestamp: number;
  mdLabels: string[];
  previewCount: number;
  hoveredAssetData: any;
  videoSrc: string | null;
  showSimilarityButton: boolean;
  boundingBoxes: number[][] | null;
  type: "IMAGE" | "OBJECT" | "VIDEO";
  onLinkClick: () => void;
  onSimilarityClick: () => void;
  onDetailsClick: (e: any) => void;
}

const Body = ({
  type,
  mdLabels,
  imageSrc,
  videoSrc,
  timestamp,
  linkClicked,
  previewCount,
  boundingBoxes,
  hoveredAssetData,
  showSimilarityButton,
  onLinkClick,
  onDetailsClick,
  onSimilarityClick,
}: BodyProps) => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [currTime, setCurrTime] = useState(0);

  const { enrichDatasetId } = useParams();

  const { error, id: hoveredId } = hoveredAssetData;

  const showVideo = !error && videoLoaded;
  const showTimestamp = !!timestamp && showVideo;
  const showActionButtons =
    !(error || videoError) && hoveredId && !enrichDatasetId;
  const VIDEO_DURATION = 4;
  const showLoadingOverLay = !!videoSrc && !videoLoaded && !videoError;
  const timestampText = `Timestamp ${secondsToTimeString(currTime)}`;

  const cardBodyClassnames = classNames(styles.cardBody, {
    [styles.defaultCursor]: !!enrichDatasetId,
  });

  const timestampClassNames = classNames(styles.assetPreviewTimestamp, {
    [styles.higherMarginBottom]: previewCount > 2,
  });

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  const handleVideoError = () => {
    setVideoError(true);
  };

  const handleTimeUpdate = (newTime: number) => {
    if (newTime) {
      let temp = Math.floor(newTime);
      if (temp !== currTime) {
        setCurrTime(temp);
      }
    } else setCurrTime(0);
  };

  const onUpdateAssetData = () => {};

  //handle the video reloads when selection is made from carousel
  useEffect(() => {
    setVideoError(false);
    setVideoLoaded(false);
  }, [hoveredId, videoSrc]);

  const renderPreview = () => {
    if (videoError) {
      return <ErrorPreview />;
    }

    switch (type) {
      case "OBJECT":
      case "IMAGE":
        return (
          <ImagePreview
            hoverDelay={0}
            src={imageSrc}
            labels={mdLabels}
            showAssetPreview={false}
            onUpdateAssetData={onUpdateAssetData}
            boundingBoxes={boundingBoxes || []}
          />
        );

      case "VIDEO":
        return (
          <>
            {showLoadingOverLay && (
              <div className={styles.loadingOverlay}>
                <Loader />
              </div>
            )}

            <VideoBox
              src={videoSrc}
              offset={timestamp || 0}
              duration={VIDEO_DURATION}
              showVideoBox={videoLoaded}
              handleVideoLoad={handleVideoLoad}
              handleVideoError={handleVideoError}
              handleTimeUpdate={handleTimeUpdate}
            />
          </>
        );
    }
  };

  return (
    <div
      onClick={onDetailsClick}
      className={cardBodyClassnames}
      style={{ backgroundImage: `url(${imageSrc})` }}
    >
      {renderPreview()}

      {showActionButtons && (
        <ActionButtons
          linkClicked={linkClicked}
          showSimilarityButton={showSimilarityButton}
          onLinkClick={onLinkClick}
          onSimilarityClick={onSimilarityClick}
        />
      )}

      {showTimestamp && (
        <div className={timestampClassNames}>{timestampText}</div>
      )}
    </div>
  );
};

export default Body;
